import React, { useState, useEffect } from 'react';
import { FileText, Users, Calendar, CheckCircle, XCircle, Clock } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { format } from 'date-fns';

interface Contract {
  id: string;
  title: string;
  description: string;
  status: string;
  created_at: string;
  valid_until: string;
  participants: {
    id: string;
    email: string;
    role: string;
    status: string;
  }[];
}

export function ContractList() {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'draft' | 'active' | 'completed'>('all');

  useEffect(() => {
    fetchContracts();
  }, [filter]);

  const fetchContracts = async () => {
    try {
      // First fetch the contracts
      let contractsQuery = supabase
        .from('contracts')
        .select('*');

      if (filter !== 'all') {
        contractsQuery = contractsQuery.eq('status', filter);
      }

      const { data: contractsData, error: contractsError } = await contractsQuery;
      if (contractsError) throw contractsError;

      // Then fetch participants for each contract
      const contractsWithParticipants = await Promise.all(
        contractsData.map(async (contract) => {
          const { data: participantsData, error: participantsError } = await supabase
            .from('contract_participants')
            .select(`
              id,
              role,
              status,
              user_id,
              profiles!inner (
                email
              )
            `)
            .eq('contract_id', contract.id);

          if (participantsError) {
            console.error('Error fetching participants:', participantsError);
            return {
              ...contract,
              participants: []
            };
          }

          return {
            ...contract,
            participants: participantsData.map(p => ({
              id: p.id,
              email: p.profiles.email,
              role: p.role,
              status: p.status
            }))
          };
        })
      );

      setContracts(contractsWithParticipants);
    } catch (error) {
      console.error('Error fetching contracts:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'draft':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'active':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-blue-500" />;
      case 'cancelled':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <Clock className="w-6 h-6 animate-spin text-primary-600 mx-auto mb-2" />
        <p className="text-gray-600">Loading contracts...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Contracts</h2>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as typeof filter)}
          className="px-3 py-2 border rounded-md"
        >
          <option value="all">All Contracts</option>
          <option value="draft">Drafts</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
        </select>
      </div>

      {contracts.length === 0 ? (
        <div className="text-center py-8 text-gray-600">
          No contracts found
        </div>
      ) : (
        <div className="space-y-4">
          {contracts.map(contract => (
            <div key={contract.id} className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold">{contract.title}</h3>
                  {contract.description && (
                    <p className="text-gray-600 mt-1">{contract.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {getStatusIcon(contract.status)}
                  <span className="font-medium capitalize">{contract.status}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="w-5 h-5" />
                  <span>Created: {format(new Date(contract.created_at), 'MMM d, yyyy')}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="w-5 h-5" />
                  <span>Valid until: {format(new Date(contract.valid_until), 'MMM d, yyyy')}</span>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="font-medium">Participants</h4>
                {contract.participants.map((participant, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-gray-500" />
                      <span>{participant.email}</span>
                      <span className="text-sm text-gray-500">({participant.role})</span>
                    </div>
                    <div className={`px-2 py-1 rounded-full text-sm ${
                      participant.status === 'approved'
                        ? 'bg-green-100 text-green-700'
                        : participant.status === 'rejected'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {participant.status}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center gap-2"
                >
                  <FileText className="w-5 h-5" />
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}